/**
 * ReferenceValueCombobox — free-text input + suggestion panel, for a
 * reference-data category whose list can legitimately be EMPTY (only
 * `season` today: it has no static seed by design — the backend live-scans
 * existing matches for distinct seasons, so a fresh install starts with
 * zero values). `ReferenceValueSelect` (a closed dropdown) would hard-block
 * data entry on a fresh install for a category like that — this component
 * can't, because a plain text input is never constrained to a known list.
 *
 * Do NOT use this for a genuinely closed set (season/competition_stage/
 * match_type/venue_surface's siblings all have a static default seed and
 * are never empty) — use `ReferenceValueSelect` for those.
 *
 * No deprecated-value-preservation logic needed here (unlike
 * `ReferenceValueSelect`): a free-text input can never silently drop the
 * current value — whatever was typed/stored stays exactly as typed. The
 * suggestion panel only offers ACTIVE values (same "not offered for a new
 * pick" rule as the select components), it never constrains input.
 *
 * PMA-UI wave: built on the `Combobox` primitive (`components/ui/combobox.tsx`,
 * Radix Popover for positioning/portal/dismissal only — the input itself is
 * still a plain, real `<input>`). Public props and observable behavior are
 * unchanged from the PMA-REF version — see `ReferenceValueCombobox.test.tsx`.
 */

import { useTranslation } from "react-i18next";
import {
  resolveReferenceLabel,
  useReferenceValues,
} from "../hooks/useReferenceValues";
import type { ReferenceCategory } from "../lib/referenceDataApi";
import { Combobox } from "./ui/combobox";

interface ReferenceValueComboboxProps {
  category: ReferenceCategory;
  value: string;
  onChange: (value: string) => void;
  id: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
  "data-testid"?: string;
}

export default function ReferenceValueCombobox({
  category,
  value,
  onChange,
  id,
  required,
  disabled,
  className,
  placeholder,
  "data-testid": dataTestId,
}: ReferenceValueComboboxProps) {
  const { t, i18n } = useTranslation("admin");
  const { data } = useReferenceValues(category, { includeInactive: false });

  const options = (data ?? []).map((v) => ({
    value: v.code,
    label: resolveReferenceLabel(v, i18n.language),
  }));

  return (
    <Combobox
      id={id}
      value={value}
      onChange={onChange}
      options={options}
      required={required}
      disabled={disabled}
      placeholder={placeholder}
      className={className}
      data-testid={dataTestId}
      emptyMessage={t("referenceData.noSuggestions")}
    />
  );
}
